// Merken-Buttons (Lesezeichen) und Merklisten-Zähler im Header, auf jeder Seite (bundle.js).
// Zustand und Zahl kommen immer aus dem Store, nie aus einem eigenen Zähler.
import { getFavoritesStore } from "./store.mjs";
import { notify, showError, t } from "./feedback";
import { trackFavoriteEvent } from "./analytics.mjs";

const BUTTON = ".ws-favorite-toggle";

function initFavorites() {
  const store = getFavoritesStore();
  const known = new WeakSet();
  function pop(button, added) {
    const targets = [
      button,
      ...document.querySelectorAll("[data-favorites-nav]"),
    ];
    for (const target of targets) {
      target.classList.remove("is-popping");
      if (
        !added ||
        !target.getClientRects().length ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      )
        continue;
      // Restart the CSS animation when the same heart is pressed twice.
      void target.offsetWidth;
      target.classList.add("is-popping");
      target.addEventListener(
        "animationend",
        () => target.classList.remove("is-popping"),
        { once: true },
      );
    }
  }
  function update({ items }) {
    const ids = new Set(items.map((item) => item.id));
    for (const button of document.querySelectorAll(BUTTON)) {
      const saved = ids.has(Number(button.dataset.id));
      button.setAttribute("aria-pressed", String(saved));
      const action = saved ? t("remove") : t("save");
      button.setAttribute("aria-label", `${action}: ${button.dataset.title}`);
      const label = button.querySelector(".ws-favorite-toggle__label");
      if (label) label.textContent = saved ? t("saved") : t("save");
    }
    for (const nav of document.querySelectorAll("[data-favorites-nav]")) {
      nav.classList.toggle("has-favorites", items.length > 0);
      nav.setAttribute("aria-label", `${t("favorites")} (${items.length})`);
      const count = nav.querySelector("[data-favorites-count]");
      if (count) {
        count.textContent = items.length > 99 ? "99+" : String(items.length);
        count.hidden = items.length === 0;
      }
    }
  }
  function discover(root) {
    const buttons = root.matches?.(BUTTON)
      ? [root]
      : [...root.querySelectorAll(BUTTON)];
    const fresh = buttons.filter((button) => !known.has(button));
    if (!fresh.length) return;
    fresh.forEach((button) => known.add(button));
    // Saved titles and links follow renames as soon as a heart is seen again.
    store.updateDetails(
      fresh.map((button) => ({
        id: button.dataset.id,
        title: button.dataset.title,
        url: button.dataset.url,
      })),
    );
    update(store.getState());
  }
  store.subscribe(update);
  discover(document);
  // The event archive and the restaurant filter replace cards without a navigation.
  new MutationObserver((records) => {
    for (const record of records)
      for (const node of record.addedNodes)
        if (node.nodeType === 1) discover(node);
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener("click", (event) => {
    const button = event.target.closest(BUTTON);
    if (!button) return;
    event.preventDefault();
    const result = store.toggle({
      id: button.dataset.id,
      title: button.dataset.title,
      url: button.dataset.url,
    });
    if (!result.ok) return showError(result.error);
    if (result.added) trackFavoriteEvent("saved");
    pop(button, result.added);
    notify(result.added ? t("added") : t("removed"), {
      open: result.added,
      undo: result.added ? null : result,
    });
  });
}

if (document.readyState === "loading")
  document.addEventListener("DOMContentLoaded", initFavorites, { once: true });
else initFavorites();
